'use client';

import { AuthHandoffSurface } from '@/components/auth/auth-handoff-surface';
import { AuthPage } from '@/components/auth/auth-page';
import { getAuthPageCopy } from '@/i18n/auth-page-copy';
import { useAuth } from '@/lib/auth';
import { pushAuthHandoff } from '@/lib/auth/auth-handoff';
import { AUTH_HANDOFF_PENDING_MAX_MS } from '@/lib/auth/auth-handoff-phase';
import { RenderIdentityContext } from '@/providers/render-identity';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useContext, useEffect, useState } from 'react';

type AuthRouteViewProps = {
  authBasePath: string;
  defaultMode: 'signIn' | 'signUp';
  signInPath?: string;
  signUpPath?: string;
};

export function AuthRouteView({
  authBasePath,
  defaultMode,
  signInPath,
  signUpPath,
}: AuthRouteViewProps) {
  // Leave only for a confirmed viewer, the same signal /auth/signing-in
  // waits for. Convex can still hold a signed-out account's token while that
  // account's session is gone; trusting it bounced people between this page
  // and /auth/signing-in forever (#3563).
  const { isAuthenticated, isLoading } = useAuth();
  // A request that carried a session token paints the handoff surface from
  // the first frame, so a signed-in visitor never sees the form (#5559). If
  // the session turns out gone (#3563) or never resolves, the form shows.
  const sessionHint = useContext(RenderIdentityContext) !== null;
  const [hintExpired, setHintExpired] = useState(false);
  useEffect(() => {
    if (!sessionHint) return;
    const timer = window.setTimeout(
      () => setHintExpired(true),
      AUTH_HANDOFF_PENDING_MAX_MS
    );
    return () => window.clearTimeout(timer);
  }, [sessionHint]);
  const restoring =
    sessionHint && !hintExpired && (isLoading || isAuthenticated);
  const label = getAuthPageCopy(useLocale()).loggingYouIn;

  // The page stays mounted while it redirects, so the card can recede under
  // the handoff veil instead of being swapped for a loader (#5559).
  return (
    <>
      {isAuthenticated ? <AuthenticatedRedirect /> : null}
      <AuthPage
        authBasePath={authBasePath}
        defaultMode={defaultMode}
        signInPath={signInPath}
        signUpPath={signUpPath}
      />
      {restoring ? <AuthHandoffSurface label={label} /> : null}
    </>
  );
}

function AuthenticatedRedirect() {
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const query = searchParams.toString();
    // The form recedes under the handoff veil (#5559).
    pushAuthHandoff(
      router,
      query ? `/auth/signing-in?${query}` : '/auth/signing-in'
    );
  }, [router, searchParams]);

  return null;
}
